import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { activateWorkflow, trackContentUsage } from "@/lib/workflowHelpers";
import { CheckCircle2, AlertCircle, Rocket, ChevronLeft, ChevronRight, BookOpen, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MemberDataFields } from "./MemberDataFields";
import { OfficerDataFields } from "./OfficerDataFields";
import { TextareaArrayField } from "./forms/TextareaArrayField";
import { DistributionListSelector } from "./DistributionListSelector";
import { ConductingMeetingDataFields } from "./ConductingMeetingDataFields";
import { ResolutionCategorySelect } from "./ResolutionCategorySelect";
import { RecordJournalDataFields } from "./record-journal/RecordJournalDataFields";
import { RecordJournalTypeSelect } from "./record-journal/RecordJournalTypeSelect";

interface SuggestedMotion {
  motion_text: string;
  description?: string;
  is_required?: boolean;
}

interface WorkflowLaunchWizardProps {
  workflow: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skipNavigation?: boolean;
  suggestedMotions?: SuggestedMotion[];
  initialWorkflowData?: Record<string, any>;
}

export const WorkflowLaunchWizard = ({
  workflow: workflowProp,
  open,
  onOpenChange,
  skipNavigation = false,
  suggestedMotions,
  initialWorkflowData,
}: WorkflowLaunchWizardProps) => {
  // Support Hub Stage 5, Part D: route legacy conducting_a_meeting to recording_minutes
  // so both entry points share the same recording track.
  const [reroutedWorkflow, setReroutedWorkflow] = useState<any | null>(null);
  const workflow = reroutedWorkflow ?? workflowProp;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (workflowProp?.workflow_key === 'conducting_a_meeting') {
        const { data } = await supabase
          .from('workflows')
          .select('*')
          .eq('workflow_key', 'recording_minutes')
          .eq('is_active', true)
          .maybeSingle();
        if (!cancelled && data) setReroutedWorkflow(data);
      } else if (reroutedWorkflow) {
        setReroutedWorkflow(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowProp?.id, workflowProp?.workflow_key]);
  console.log('🔍 WorkflowLaunchWizard - suggestedMotions received:', suggestedMotions);
  console.log('🔍 WorkflowLaunchWizard - workflow_key:', workflow?.workflow_key);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [prerequisitesChecked, setPrerequisitesChecked] = useState<Record<number, boolean>>({});
  const [workflowData, setWorkflowData] = useState<Record<string, any>>({});
  const [selectedUnaId, setSelectedUnaId] = useState<string>("");
  const [unas, setUnas] = useState<any[]>([]);
  const [unaMembers, setUnaMembers] = useState<any[]>([]);
  const [treasurySecretary, setTreasurySecretary] = useState<{ id: string; name: string; role: string } | null>(null);

  // Helper to format member role for display
  const formatMemberRole = (role: string | null | undefined): string => {
    if (!role) return 'Member';
    const roleMap: Record<string, string> = {
      'treasury_secretary': 'Secretary Treasurer',
      'managing_member': 'Managing Member',
      'board_member_active': 'Board Member',
      'board_member_passive': 'Board Member',
      'operations_manager': 'Operations Manager',
      'advisor': 'Advisor',
      'observer': 'Observer',
    };
    return roleMap[role.toLowerCase()] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Record Journal workflow has an extra step for type selection
  const isRecordJournalWorkflow = workflow.workflow_key === 'record_journal_transaction';
  const totalSteps = isRecordJournalWorkflow ? 4 : 3;
  const prerequisites = Array.isArray(workflow.prerequisites) ? workflow.prerequisites : [];
  const phases = Array.isArray(workflow.phases) ? workflow.phases : [];

  useEffect(() => {
    loadUserUnas();
  }, []);

  useEffect(() => {
    if (selectedUnaId) {
      loadUnaMembers(selectedUnaId);
    }
  }, [selectedUnaId]);

  // Reset wizard state whenever the dialog opens
  useEffect(() => {
    if (open) {
      // Reset to fresh state on every open
      setCurrentStep(1);
      setPrerequisitesChecked({});
      
      // Initialize workflowData with template defaults + initialWorkflowData
      const defaultValues: Record<string, any> = {};
      
      workflow?.form_fields?.forEach((field: any) => {
        if (field.default_value !== undefined && field.default_value !== null) {
          if (field.default_value === 'today') {
            defaultValues[field.field_name] = new Date().toISOString().split('T')[0];
          } else {
            defaultValues[field.field_name] = field.default_value;
          }
        }
      });
      
      // Merge with initialWorkflowData (takes precedence over form defaults)
      const mergedValues = { ...defaultValues, ...initialWorkflowData };
      setWorkflowData(mergedValues);
      
      console.log('✅ Wizard opened - initialized workflow data:', mergedValues);
      if (initialWorkflowData && Object.keys(initialWorkflowData).length > 0) {
        console.log('✅ Pre-populated from initialWorkflowData:', initialWorkflowData);
      }
    }
  }, [open, workflow?.form_fields, initialWorkflowData]);

  const loadUserUnas = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("unas")
      .select("id, name")
      .eq("treasury_secretary_id", user.id);

    if (error) {
      console.error("Error loading UNAs:", error);
      return;
    }

    setUnas(data || []);
    if (data && data.length > 0) {
      setSelectedUnaId(data[0].id);
      console.log('Auto-selected UNA:', data[0]?.name);
    }
  };

  const loadUnaMembers = async (unaId: string) => {
    const { data, error } = await supabase
      .from("una_members")
      .select("id, full_name, email, member_role, role")
      .eq("una_id", unaId)
      .eq("status", "active")
      .order("full_name");

    if (error) {
      console.error("Error loading UNA members:", error);
      return;
    }

    setUnaMembers(data || []);

    // Also fetch Secretary Treasurer and primary_officer_role for this UNA
    const { data: unaData, error: unaError } = await supabase
      .from("unas")
      .select("treasury_secretary_id, primary_officer_role")
      .eq("id", unaId)
      .single();

    if (unaError || !unaData?.treasury_secretary_id) {
      console.error("Error loading Secretary Treasurer:", unaError);
      setTreasurySecretary(null);
      return;
    }

    const primaryOfficerRole = unaData.primary_officer_role || 'Secretary Treasurer';

    // Fetch Secretary Treasurer's profile
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("id, full_name")
      .eq("id", unaData.treasury_secretary_id)
      .single();

    if (profileError || !profileData) {
      console.error("Error loading Secretary Treasurer profile:", profileError);
      setTreasurySecretary(null);
      return;
    }

    setTreasurySecretary({
      id: profileData.id,
      name: profileData.full_name || primaryOfficerRole,
      role: primaryOfficerRole
    });
    console.log('Loaded Primary Officer:', profileData.full_name, 'Role:', primaryOfficerRole);
  };

  const shouldShowField = (field: any): boolean => {
    // Handle both naming conventions: show_when and conditional_on
    const showWhen = field.show_when || field.conditional_on;
    if (!showWhen) return true;
    
    const { field: dependentField, value: expectedValue } = showWhen;
    const actualValue = workflowData[dependentField];
    
    return actualValue === expectedValue;
  };

  // Helper to get field label with fallback
  const getFieldLabel = (field: any): string => field.field_label || field.label || '';
  const getFieldRequired = (field: any): boolean => field.is_required ?? field.required ?? false;
  const getFieldOptions = (field: any): string[] => field.field_options?.options || field.options || [];
  const getFieldPlaceholder = (field: any): string => field.field_options?.placeholder || field.placeholder || '';
  const getFieldRows = (field: any): number => field.field_options?.rows || field.rows || 3;

  const allPrerequisitesChecked = () => {
    if (prerequisites.length === 0) return true;
    return prerequisites.every((_, index) => prerequisitesChecked[index]);
  };

  const handleNext = () => {
    if (currentStep === 1 && !allPrerequisitesChecked()) {
      toast.error("Please confirm all prerequisites before proceeding");
      return;
    }
    // For record journal workflow, validate record type selection on step 2
    if (isRecordJournalWorkflow && currentStep === 2 && !workflowData.record_type) {
      toast.error("Please select a record type to continue");
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleLaunch = async () => {
    if (!selectedUnaId) {
      toast.error("Please select a UNA");
      return;
    }

    // ✅ Validate required fields for member workflow
    if (workflow.workflow_key === 'add_member_simple') {
      const requiredFields = ['member_full_name', 'member_address', 'member_role'];
      const missingFields = requiredFields.filter(field => {
        const value = workflowData[field];
        return value == null || (typeof value === 'string' && value.trim() === '');
      });
      
      if (missingFields.length > 0) {
        toast.error(`Please fill in required fields: ${missingFields.join(', ')}`);
        console.error('❌ Missing required fields:', missingFields);
        return;
      }
      
      console.log('✅ All required fields present:', requiredFields);
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      console.log('=== WORKFLOW LAUNCH DEBUG ===');
      console.log('1. Raw workflow data:', workflowData);
      console.log('2. Member role value:', workflowData.member_role);
      console.log('3. All workflow data keys:', Object.keys(workflowData));
      console.log('4. Critical member fields:', {
        member_full_name: workflowData.member_full_name,
        member_first_name: workflowData.member_first_name,
        member_last_name: workflowData.member_last_name,
        member_role: workflowData.member_role,
        member_address: workflowData.member_address,
      });

      // Create the task
      const { data: task, error: taskError } = await supabase
        .from("custom_tasks")
        .insert({
          task_title: workflow.title,
          task_description: workflow.description,
          user_id: user.id,
          una_id: selectedUnaId,
          is_from_workflow: true,
          workflow_id: workflow.id,
          workflow_completion_percentage: 0,
          status: "in_progress",
        })
        .select()
        .single();

      if (taskError) throw taskError;

      console.log('4. Task created with ID:', task.id);

      // Activate the workflow
      const result = await activateWorkflow(workflow.id, task.id, workflowData);
      
      if (!result.success) {
        throw new Error(result.error || "Failed to activate workflow");
      }

      console.log('5. Workflow activated successfully');

      // CRITICAL FIX: Create meeting_minutes record for meeting workflows
      if (workflow.workflow_key === 'recording_minutes' || workflow.workflow_key === 'conducting_a_meeting') {
        console.log('==========================================');
        console.log('RECORDING MINUTES WORKFLOW - STARTING');
        console.log('==========================================');
        
        // Step 1: Validate required fields FIRST (fail fast)
        // ConductingMeetingDataFields uses: meeting_time, meeting_conductor_id, required_attendees
        // RecordingMinutes uses: meeting_start_time, presiding_officer, expected_attendees
        const requiredFields = workflow.workflow_key === 'conducting_a_meeting' 
          ? [
              'meeting_type',
              'meeting_date', 
              'meeting_time',
              'meeting_location',
              'meeting_conductor_id',
              'required_attendees'
            ]
          : [
              'meeting_type',
              'meeting_date', 
              'meeting_start_time',
              'meeting_location',
              'presiding_officer',
              'expected_attendees'
            ];

        // For conducting_a_meeting, required_attendees is an array - check length
        const missingFields = requiredFields.filter(f => {
          const value = workflowData[f];
          if (Array.isArray(value)) return value.length === 0;
          return !value;
        });

        if (missingFields.length > 0) {
          console.error('❌ Missing required fields:', missingFields);
          console.error('Current workflowData:', workflowData);
          
          // Map field names to user-friendly labels
          const fieldLabels: Record<string, string> = {
            meeting_type: 'Meeting Type',
            meeting_date: 'Meeting Date',
            meeting_time: 'Meeting Time',
            meeting_start_time: 'Meeting Time',
            meeting_location: 'Meeting Location',
            meeting_conductor_id: 'Meeting Conductor',
            presiding_officer: 'Presiding Officer',
            required_attendees: 'Required Attendees',
            expected_attendees: 'Expected Attendees',
          };
          
          const missingLabels = missingFields.map(f => fieldLabels[f] || f);
          toast.error(`Please fill in required fields: ${missingLabels.join(', ')}`, {
            duration: 5000,
          });
          setLoading(false);
          return;
        }

        console.log('✅ All required fields present');
        console.log('workflowData:', JSON.stringify(workflowData, null, 2));

        // Step 2: Check authorization (Secretary Treasurer OR una_member)
        console.log('Checking authorization for user_id:', user.id, 'una_id:', selectedUnaId);
        
        let recordingSecretaryId: string | null = null;

        // First check if user is Secretary Treasurer
        const { data: unaData } = await supabase
          .from('unas')
          .select('treasury_secretary_id')
          .eq('id', selectedUnaId)
          .single();

        const isTreasurySecretary = unaData?.treasury_secretary_id === user.id;

        if (!isTreasurySecretary) {
          // Not Secretary Treasurer - must be in una_members
          const { data: currentMember, error: memberError } = await supabase
            .from('una_members')
            .select('id, full_name, role')
            .eq('una_id', selectedUnaId)
            .eq('user_id', user.id)
            .single();

          if (memberError || !currentMember) {
            console.error('❌ Could not find una_member record:', memberError);
            toast.error("You must be a member of this UNA to record minutes");
            setLoading(false);
            return;
          }

          console.log('✅ Found una_member:', currentMember.id, '-', currentMember.full_name);
          recordingSecretaryId = currentMember.id;
        } else {
          console.log('✅ User is Secretary Treasurer - authorized to record minutes');
          recordingSecretaryId = null; // Secretary Treasurer doesn't have una_members ID
        }

        // Step 3: NORMALIZE FIELDS - conducting_a_meeting uses different field names than recording_minutes
        // DB requires: meeting_start_time, presiding_officer (for lookup), expected_attendees
        // conducting_a_meeting UI uses: meeting_time, meeting_conductor_id, required_attendees + optional_attendees
        const normalizedMeetingStartTime = workflowData.meeting_start_time ?? workflowData.meeting_time;
        const normalizedPresidingOfficer = workflowData.presiding_officer ?? workflowData.meeting_conductor_id;
        
        // For conducting_a_meeting, merge required + optional attendees
        let normalizedExpectedAttendees: any[];
        if (workflow.workflow_key === 'conducting_a_meeting') {
          const required = workflowData.required_attendees || [];
          const optional = workflowData.optional_attendees || [];
          normalizedExpectedAttendees = [...required, ...optional];
        } else {
          normalizedExpectedAttendees = workflowData.expected_attendees || [];
        }

        console.log('🔄 Normalized meeting fields:', {
          meeting_start_time: normalizedMeetingStartTime,
          presiding_officer: normalizedPresidingOfficer,
          expected_attendees_count: normalizedExpectedAttendees.length
        });

        // FAIL FAST: If meeting_start_time is still missing after normalization, stop here
        if (!normalizedMeetingStartTime) {
          console.error('❌ CRITICAL: meeting_start_time is still null after normalization');
          console.error('workflowData.meeting_start_time:', workflowData.meeting_start_time);
          console.error('workflowData.meeting_time:', workflowData.meeting_time);
          toast.error("Meeting time is required. Please go back and select a time.");
          setLoading(false);
          return;
        }

        // Determine presiding_officer_id (null if Secretary Treasurer selected)
        const presidingOfficerIsTreasSecy = unaData?.treasury_secretary_id === normalizedPresidingOfficer;
        const presidingOfficerId = presidingOfficerIsTreasSecy ? null : normalizedPresidingOfficer;

        // Step 4: Create meeting_minutes record
        // Pre-populate motions from suggested resolutions if provided
        console.log('🔍 DEBUG - suggestedMotions at insert time:', suggestedMotions);
        console.log('🔍 DEBUG - suggestedMotions length:', suggestedMotions?.length);
        
        // First-meeting mode applies ONLY when the UNA has NO prior certified
        // meeting_minutes. The meeting-type selector has no explicit
        // "organizational" option, so a UNA that has already certified any
        // prior meeting is never in first-meeting mode again — regardless of
        // meeting type or attendee count. This prevents a "Special Meeting"
        // months later from being treated as the organizational meeting.
        const { count: priorCertifiedCount, error: priorCertifiedError } = await supabase
          .from('meeting_minutes')
          .select('id', { count: 'exact', head: true })
          .eq('una_id', selectedUnaId)
          .not('certified_at', 'is', null);

        if (priorCertifiedError) {
          console.error('❌ Could not check for prior certified minutes:', priorCertifiedError);
          toast.error('Could not verify meeting history. Please try again.');
          setLoading(false);
          return;
        }

        const isFirstMeeting = (priorCertifiedCount ?? 0) === 0;

        console.log('🎯 Is First Meeting Mode:', isFirstMeeting, '(prior certified minutes:', priorCertifiedCount, ')');
        
        // Get Primary Officer name and role for auto-filling voting
        const primaryOfficerRole = treasurySecretary?.role || 'Secretary Treasurer';
        let primaryOfficerName = treasurySecretary?.name || primaryOfficerRole;
        
        // For first meetings, auto-fill all voting details since it's a single-person organizational meeting
        const prePopulatedMotions = suggestedMotions?.map(m => ({
          motion_text: m.motion_text,
          description: m.description || '',
          made_by: isFirstMeeting ? primaryOfficerName : '',
          seconded_by: isFirstMeeting ? primaryOfficerName : '',  // Self-seconded for single-person meeting
          discussion_summary: isFirstMeeting ? 'Organizational resolution adopted without discussion.' : '',
          vote_result: isFirstMeeting ? 'passed' : '',
          votes_for: isFirstMeeting ? 1 : 0,
          votes_against: 0,
          abstentions: 0,
          is_suggested: true,
          is_required: m.is_required ?? false,
          timestamp: new Date().toISOString()
        })) || [];

        // Pre-populate agenda items from suggested resolutions
        const prePopulatedAgenda = suggestedMotions?.map((m, index) => ({
          title: m.motion_text,
          description: m.description || '',
          presenter: isFirstMeeting ? primaryOfficerName : '',
          time_allocated: '5 min',
          order: index + 1,
          completed: isFirstMeeting, // Auto-complete for first meeting
          discussion_notes: isFirstMeeting ? 'Resolution adopted during organizational meeting.' : '',
          is_suggested: true,
          is_required: m.is_required ?? false
        })) || [];
        
        // For first meetings, pre-populate actual_attendees with Primary Officer already checked in
        const prePopulatedActualAttendees = isFirstMeeting ? [{
          member_id: `officer_${unaData?.treasury_secretary_id}`,
          member_name: primaryOfficerName,
          member_role: primaryOfficerRole,
          is_officer: true,
          checked_in_at: new Date().toISOString(),
        }] : [];

        console.log('🔍 DEBUG - prePopulatedMotions:', prePopulatedMotions);
        console.log('🔍 DEBUG - prePopulatedAgenda:', prePopulatedAgenda);

        // Pre-fill general notes for first meetings
        const preFilledNotes = isFirstMeeting 
          ? `First organizational meeting of the UNA. Meeting conducted by the ${primaryOfficerRole} to adopt foundational resolutions and establish the organization's governance structure. All resolutions were passed unanimously by the sole board member present.`
          : '';
        
        const insertObject = {
          una_id: selectedUnaId,
          task_id: task.id,
          meeting_type: workflowData.meeting_type,
          meeting_date: workflowData.meeting_date,
          meeting_start_time: normalizedMeetingStartTime, // USE NORMALIZED VALUE
          meeting_location: workflowData.meeting_location,
          meeting_purpose: workflowData.meeting_purpose || '',
          presiding_officer_id: presidingOfficerId,
          recording_secretary_id: recordingSecretaryId,
          expected_attendees: normalizedExpectedAttendees, // USE NORMALIZED VALUE
          actual_attendees: prePopulatedActualAttendees, // Pre-fill for first meeting
          special_guests: workflowData.special_guests || [],
          quorum_required: isFirstMeeting ? false : (workflowData.quorum_required || false), // No quorum for single-person meetings
          quorum_threshold: isFirstMeeting ? null : (workflowData.quorum_threshold || null),
          quorum_met: isFirstMeeting ? true : null, // Auto-set for first meeting
          has_transcript: workflowData.has_transcript || false,
          transcript_text: workflowData.transcript_text || '',
          transcript_file_url: workflowData.transcript_file || '',
          agenda_items: [...prePopulatedAgenda, ...(workflowData.agenda_items || [])],
          motions: prePopulatedMotions,
          general_notes: preFilledNotes,
          is_first_meeting: isFirstMeeting, // Flag for UI to show helper text
          status: 'recording'
        };

        console.log('INSERT object:', JSON.stringify(insertObject, null, 2));

        const { data: meetingRecord, error: meetingError } = await supabase
          .from('meeting_minutes')
          .insert(insertObject)
          .select()
          .single();
          
        if (meetingError) {
          console.error('❌ Failed to create meeting minutes:', meetingError);
          
          // CLEANUP: Delete orphaned records from failed launch
          console.log('🧹 Cleaning up orphaned records for task:', task.id);
          await supabase.from('task_phases').delete().eq('task_id', task.id);
          await supabase.from('task_workflows').delete().eq('task_id', task.id);
          await supabase.from('custom_tasks').delete().eq('id', task.id);
          
          throw meetingError;
        }

        console.log('✅ Meeting minutes record created:', meetingRecord.id);

        // Step 4: Update task_workflows with meeting_minutes_id
        const { error: updateError } = await supabase
          .from('task_workflows')
          .update({ meeting_minutes_id: meetingRecord.id })
          .eq('task_id', task.id);
        
        if (updateError) {
          console.error('❌ Failed to link meeting_minutes to task_workflows:', updateError);
        } else {
          console.log('✅ Meeting minutes linked to task_workflows');
        }

        // Auto-complete Phase 1 since the wizard setup is essentially "Prepare Meeting Materials"
        console.log('📊 Auto-completing Phase 1 for meeting workflow...');
        
        // Update Phase 1 to 'completed'
        await supabase
          .from('task_phases')
          .update({ 
            status: 'completed',
            completed_at: new Date().toISOString()
          })
          .eq('task_id', task.id)
          .eq('phase_number', 1);
        
        // Update Phase 2 to 'in_progress'
        await supabase
          .from('task_phases')
          .update({ status: 'in_progress' })
          .eq('task_id', task.id)
          .eq('phase_number', 2);
        
        // Update task progress to 25% (1/4 phases complete)
        await supabase
          .from('custom_tasks')
          .update({ workflow_completion_percentage: 25 })
          .eq('id', task.id);
        
        console.log('✅ Phase 1 auto-completed, progress set to 25%');

        console.log('==========================================');
        console.log('RECORDING MINUTES WORKFLOW - COMPLETE');
        console.log('==========================================');
      }

      // Track workflow start
      await trackContentUsage("workflow", workflow.id, "started", {
        task_id: task.id,
        una_id: selectedUnaId,
      });

      console.log('✅ Workflow launched successfully, invalidating queries...');
      
      // ✅ CRITICAL: Invalidate React Query cache to trigger refetch
      await queryClient.invalidateQueries({ queryKey: ['workflowInstances'] });
      await queryClient.invalidateQueries({ queryKey: ['workflows'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['myTasks'] });

      toast.success("Workflow launched successfully!", {
        description: `"${workflow.title}" has been added to your workflows.`,
      });
      
      // Close wizard first
      onOpenChange(false);
      
      // Only navigate if not in journey context
      if (!skipNavigation) {
        navigate(`/workflows?tab=my-workflows&launched=1&wid=${workflow.id}`);
        console.log('✅ Navigation complete, new workflow should appear immediately');
      } else {
        console.log('✅ Workflow launched, skipping navigation (journey context)');
      }
    } catch (error: any) {
      console.error("Error launching workflow:", error);
      toast.error(error.message || "Failed to launch workflow");
    } finally {
      setLoading(false);
    }
  };

  const renderReviewStep = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 className="h-5 w-5 text-green-500" />
        <h3 className="font-semibold">Review & Launch</h3>
      </div>
      <div className="space-y-4">
        <div className="border rounded-lg p-4">
          <h4 className="font-medium mb-2">Workflow Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Workflow:</span>
              <span className="font-medium">{workflow.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Estimated Time:</span>
              <span>{workflow.estimated_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phases:</span>
              <span>{phases.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">UNA:</span>
              <span>{unas.find((u) => u.id === selectedUnaId)?.name}</span>
            </div>
            {isRecordJournalWorkflow && workflowData.record_type && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Record Type:</span>
                <span>{workflowData.record_type}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h4 className="font-medium mb-2">What Happens Next</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <div className="mt-1">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              </div>
              <span>A task will be created in your task list</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="mt-1">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              </div>
              <span>You'll be guided through each phase step-by-step</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="mt-1">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              </div>
              <span>Documents will be generated automatically as needed</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="mt-1">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              </div>
              <span>You can pause and resume at any time</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              <h3 className="font-semibold">Confirm Prerequisites</h3>
            </div>
            {prerequisites.length === 0 ? (
              <p className="text-muted-foreground">No prerequisites required for this workflow.</p>
            ) : (
              <div className="space-y-3">
                {prerequisites.map((prereq: string, index: number) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                    <Checkbox
                      id={`prereq-${index}`}
                      checked={prerequisitesChecked[index] || false}
                      onCheckedChange={(checked) =>
                        setPrerequisitesChecked((prev) => ({
                          ...prev,
                          [index]: checked as boolean,
                        }))
                      }
                    />
                    <label
                      htmlFor={`prereq-${index}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {prereq}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 2:
        // For Record Journal workflow: Step 2 is ONLY record type selection
        if (isRecordJournalWorkflow) {
          return (
            <div className="space-y-4">
              <h3 className="font-semibold mb-4">Select Record Type</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Choose the type of transaction or record you want to document. Different fields will be collected based on your selection.
              </p>
              <RecordJournalTypeSelect
                value={workflowData.record_type || ''}
                onChange={(value) => setWorkflowData(prev => ({ ...prev, record_type: value }))}
              />
            </div>
          );
        }
        
        // For all other workflows: Step 2 is data entry
        const formFields = Array.isArray(workflow.form_fields) ? workflow.form_fields : [];
        
        return (
          <div className="space-y-4">
            <h3 className="font-semibold mb-4">Workflow Information</h3>
            
            {/* Resolution Writing Guide - collapsible panel */}
            {workflow.workflow_key === 'writing_resolution' && (
              <Collapsible className="mb-4">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <BookOpen className="h-5 w-5 text-primary" />
                    <span className="font-medium">Resolution Writing Guide</span>
                    <ChevronDown className="h-4 w-4 ml-auto transition-transform group-data-[state=open]:rotate-180" />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  <div className="p-4 border rounded-lg bg-muted/30 space-y-4 text-sm">
                    <div>
                      <h4 className="font-semibold mb-2">Standard Resolution Format</h4>
                      <p className="text-muted-foreground mb-2">All resolutions follow this structure:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li><strong className="text-foreground">Title</strong> - A clear, descriptive title</li>
                        <li><strong className="text-foreground">WHEREAS Clauses</strong> - Background statements explaining context</li>
                        <li><strong className="text-foreground">RESOLVED Clauses</strong> - The actual decisions and actions</li>
                        <li><strong className="text-foreground">Effective Date</strong> - When the resolution takes effect</li>
                        <li><strong className="text-foreground">Signatures</strong> - Required approval signatures</li>
                      </ol>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h4 className="font-semibold mb-2">WHEREAS Clauses</h4>
                      <p className="text-muted-foreground mb-2">Provide factual background and justification. Each clause should:</p>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
                        <li>Begin with "WHEREAS" in all capital letters</li>
                        <li>State a single fact or reason</li>
                        <li>End with semicolon (;), last clause ends with "; and"</li>
                      </ul>
                      <div className="bg-background p-3 rounded border-l-2 border-primary text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">Example:</p>
                        <p className="italic">WHEREAS, the Board has determined that additional controls are necessary;</p>
                        <p className="italic">WHEREAS, these measures will protect the Association's assets; and</p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h4 className="font-semibold mb-2">RESOLVED Clauses</h4>
                      <p className="text-muted-foreground mb-2">State the actual decisions or actions being adopted. Each clause should:</p>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
                        <li>Begin with "RESOLVED" in all capital letters</li>
                        <li>State a clear, specific action or decision</li>
                        <li>End with period (.) for the final clause</li>
                      </ul>
                      <div className="bg-background p-3 rounded border-l-2 border-primary text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">Example:</p>
                        <p className="italic">RESOLVED, that the Board hereby establishes a Finance Committee;</p>
                        <p className="italic">RESOLVED, that this resolution shall take effect immediately.</p>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h4 className="font-semibold mb-2">Best Practices</h4>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                        <li>Keep language clear, formal, and professional</li>
                        <li>Number resolutions chronologically (e.g., Resolution 2025-001)</li>
                        <li>Include the date of adoption</li>
                        <li>Obtain required signatures before finalizing</li>
                        <li>File in governance records after approval</li>
                      </ul>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
            
            {/* Member workflow data collection */}
            {workflow.workflow_key === 'add_member_simple' && (
              <MemberDataFields
                data={workflowData}
                onChange={setWorkflowData}
                unaId={selectedUnaId}
              />
            )}
            
            {/* Officer workflow data collection */}
            {(workflow.workflow_key === 'add_officer' || workflow.workflow_key === 'add_una_officer') && (
              <OfficerDataFields
                data={workflowData}
                onChange={setWorkflowData}
              />
            )}
            
            {/* Conducting Meeting workflow data collection */}
            {workflow.workflow_key === 'conducting_a_meeting' && (
              <ConductingMeetingDataFields
                workflowData={workflowData}
                setWorkflowData={setWorkflowData}
                unaId={selectedUnaId}
              />
            )}
            
            {/* Dynamic form fields for other workflows */}
            {formFields.length > 0 && !['add_member_simple', 'add_una_member_simple', 'add_officer', 'add_una_officer', 'conducting_a_meeting', 'record_journal_transaction'].includes(workflow.workflow_key) && (
              <div className="space-y-4">
                {formFields
                  .sort((a, b) => (a.field_order || 0) - (b.field_order || 0))
                  .filter(shouldShowField)
                  .map((field) => {
                    switch (field.field_type) {
                      case 'text':
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label htmlFor={field.field_name}>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground">{field.help_text}</p>
                            )}
                            <Input
                              id={field.field_name}
                              value={workflowData[field.field_name] || ''}
                              onChange={(e) => setWorkflowData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                              placeholder={getFieldPlaceholder(field)}
                              required={getFieldRequired(field)}
                            />
                          </div>
                        );
                      
                      case 'textarea':
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label htmlFor={field.field_name}>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground">{field.help_text}</p>
                            )}
                            <Textarea
                              id={field.field_name}
                              value={workflowData[field.field_name] || ''}
                              onChange={(e) => setWorkflowData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                              placeholder={getFieldPlaceholder(field)}
                              rows={getFieldRows(field)}
                              required={getFieldRequired(field)}
                            />
                          </div>
                        );
                      
                      case 'select':
                        // Special handling for resolution category
                        if (field.field_name === 'resolution_category') {
                          return (
                            <ResolutionCategorySelect
                              key={field.field_name}
                              value={workflowData[field.field_name] || field.default_value || ''}
                              onChange={(value) => setWorkflowData(prev => ({ ...prev, [field.field_name]: value }))}
                              unaId={selectedUnaId}
                              isRequired={getFieldRequired(field)}
                              helpText={field.help_text}
                            />
                          );
                        }
                        
                        // Default select rendering
                        const selectOptions = getFieldOptions(field);
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label htmlFor={field.field_name}>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground">{field.help_text}</p>
                            )}
                            <Select
                              value={workflowData[field.field_name] || field.default_value || ''}
                              onValueChange={(value) => setWorkflowData(prev => ({ ...prev, [field.field_name]: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Select ${getFieldLabel(field)}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {selectOptions.map((option: string) => (
                                  <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      
                      case 'date':
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label htmlFor={field.field_name}>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground">{field.help_text}</p>
                            )}
                            <Input
                              id={field.field_name}
                              type="date"
                              value={workflowData[field.field_name] || new Date().toISOString().split('T')[0]}
                              onChange={(e) => setWorkflowData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                              required={getFieldRequired(field)}
                            />
                          </div>
                        );
                      
                      case 'time':
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label htmlFor={field.field_name}>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground">{field.help_text}</p>
                            )}
                            <Input
                              id={field.field_name}
                              type="time"
                              value={workflowData[field.field_name] || ''}
                              onChange={(e) => setWorkflowData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                              required={getFieldRequired(field)}
                            />
                          </div>
                        );
                      
                      case 'boolean':
                        return (
                          <div key={field.field_name} className="flex items-start gap-3 p-3 border rounded-lg">
                            <Checkbox
                              id={field.field_name}
                              checked={workflowData[field.field_name] || field.default_value || false}
                              onCheckedChange={(checked) => setWorkflowData(prev => ({ ...prev, [field.field_name]: checked }))}
                            />
                            <div className="flex-1">
                              <Label htmlFor={field.field_name} className="cursor-pointer">
                                {getFieldLabel(field)}
                              </Label>
                              {field.help_text && (
                                <p className="text-sm text-muted-foreground mt-1">{field.help_text}</p>
                              )}
                            </div>
                          </div>
                        );
                      
                      case 'number':
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label htmlFor={field.field_name}>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground">{field.help_text}</p>
                            )}
                            <Input
                              id={field.field_name}
                              type="number"
                              value={workflowData[field.field_name] || ''}
                              onChange={(e) => setWorkflowData(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                              placeholder={getFieldPlaceholder(field)}
                              min={field.min_value || field.field_options?.min_value}
                              max={field.max_value || field.field_options?.max_value}
                              required={getFieldRequired(field)}
                            />
                          </div>
                        );
                      
                      case 'textarea_array':
                        return (
                          <TextareaArrayField
                            key={field.field_name}
                            label={getFieldLabel(field)}
                            value={workflowData[field.field_name] || []}
                            onChange={(value) => setWorkflowData(prev => ({ ...prev, [field.field_name]: value }))}
                            placeholder={getFieldPlaceholder(field)}
                            helpText={field.help_text}
                            minItems={field.field_options?.min_items || field.min_items}
                            maxItems={field.field_options?.max_items || field.max_items}
                            required={getFieldRequired(field)}
                          />
                        );
                      
                      case 'distribution_list':
                        return (
                          <DistributionListSelector
                            key={field.field_name}
                            label={getFieldLabel(field)}
                            value={workflowData[field.field_name] || field.default_value || ''}
                            onChange={(value) => setWorkflowData(prev => ({ ...prev, [field.field_name]: value }))}
                            unaId={selectedUnaId}
                            required={getFieldRequired(field)}
                            helpText={field.help_text}
                            allowExternal={field.field_options?.allow_external !== false}
                          />
                        );
                      
                      case 'member_select':
                        // Get filter_roles from field configuration
                        const filterRoles = field.filter_roles || field.field_options?.filter_roles || [];
                        
                        // Filter members by role if filter_roles is specified
                        let eligibleMembers = unaMembers;
                        if (filterRoles.length > 0) {
                          eligibleMembers = unaMembers.filter(member => {
                            const memberRole = (member.member_role || '').toLowerCase();
                            return filterRoles.some((role: string) => 
                              memberRole === role.toLowerCase() || 
                              memberRole.includes(role.toLowerCase().replace('_', ''))
                            );
                          });
                        }
                        
                        // Check if Secretary Treasurer should be included
                        const includeTreasurySecretary = filterRoles.length === 0 || 
                          filterRoles.some((role: string) => role.toLowerCase() === 'treasury_secretary');
                        
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label htmlFor={field.field_name}>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground">{field.help_text}</p>
                            )}
                            <Select
                              value={workflowData[field.field_name] || ''}
                              onValueChange={(value) => setWorkflowData(prev => ({ ...prev, [field.field_name]: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Select ${getFieldLabel(field)}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {/* Secretary Treasurer first if included */}
                                {includeTreasurySecretary && treasurySecretary && (
                                  <SelectItem key={treasurySecretary.id} value={treasurySecretary.id}>
                                    {treasurySecretary.name} (Secretary Treasurer)
                                  </SelectItem>
                                )}
                                {/* Filtered members */}
                                {eligibleMembers.map((member) => (
                                  <SelectItem key={member.id} value={member.id}>
                                    {member.full_name} ({formatMemberRole(member.member_role)})
                                  </SelectItem>
                                ))}
                                {/* Show message if no options */}
                                {!includeTreasurySecretary && eligibleMembers.length === 0 && (
                                  <SelectItem value="no-options" disabled>
                                    No eligible members found
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      
                      case 'member_multiselect':
                        const selectedMembers = workflowData[field.field_name] || [];
                        return (
                          <div key={field.field_name} className="space-y-2">
                            <Label>
                              {getFieldLabel(field)}
                              {getFieldRequired(field) && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.help_text && (
                              <p className="text-sm text-muted-foreground mb-2">{field.help_text}</p>
                            )}
                            <div className="border rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                              {/* Secretary Treasurer first */}
                              {treasurySecretary && (
                                <div key={treasurySecretary.id} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`${field.field_name}-${treasurySecretary.id}`}
                                    checked={selectedMembers.includes(treasurySecretary.id)}
                                    onCheckedChange={(checked) => {
                                      const newValue = checked
                                        ? [...selectedMembers, treasurySecretary.id]
                                        : selectedMembers.filter((id: string) => id !== treasurySecretary.id);
                                      setWorkflowData(prev => ({ ...prev, [field.field_name]: newValue }));
                                    }}
                                  />
                                  <Label htmlFor={`${field.field_name}-${treasurySecretary.id}`} className="cursor-pointer flex-1">
                                    {treasurySecretary.name} (Secretary Treasurer)
                                  </Label>
                                </div>
                              )}
                              {/* Regular UNA members */}
                              {unaMembers.map((member) => (
                                <div key={member.id} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`${field.field_name}-${member.id}`}
                                    checked={selectedMembers.includes(member.id)}
                                    onCheckedChange={(checked) => {
                                      const newValue = checked
                                        ? [...selectedMembers, member.id]
                                        : selectedMembers.filter((id: string) => id !== member.id);
                                      setWorkflowData(prev => ({ ...prev, [field.field_name]: newValue }));
                                    }}
                                  />
                                  <Label htmlFor={`${field.field_name}-${member.id}`} className="cursor-pointer flex-1">
                                    {member.full_name} ({formatMemberRole(member.member_role)})
                                  </Label>
                                </div>
                              ))}
                              {!treasurySecretary && unaMembers.length === 0 && (
                                <p className="text-sm text-muted-foreground">No active members found</p>
                              )}
                            </div>
                          </div>
                        );
                      
                      default:
                        return null;
                    }
                  })}
              </div>
            )}
            
            {/* Fallback for workflows without form fields */}
            {formFields.length === 0 && !['add_member_simple', 'add_una_member_simple', 'add_officer', 'add_una_officer'].includes(workflow.workflow_key) && (
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  Additional workflow configuration will be available as you progress through each phase.
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        // For Record Journal workflow: Step 3 is data entry with type-specific fields
        if (isRecordJournalWorkflow) {
          const selectedType = workflowData.record_type;
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Enter {selectedType} Details</h3>
                <Badge variant="outline">{selectedType}</Badge>
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                Complete the fields below to document this {selectedType?.toLowerCase()}.
              </p>
              <RecordJournalDataFields
                data={workflowData}
                updateField={(field, value) => setWorkflowData(prev => ({ ...prev, [field]: value }))}
                unaId={selectedUnaId}
                hideTypeSelection={true}
              />
            </div>
          );
        }
        
        // For all other workflows: Step 3 is Review
        return renderReviewStep();

      case 4:
        // Only for Record Journal workflow: Step 4 is Review
        if (isRecordJournalWorkflow) {
          return renderReviewStep();
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] md:w-full p-4 md:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl pr-8">Launch Workflow: {workflow.title}</DialogTitle>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-4 gap-2">
            <div className="flex gap-1 sm:gap-2">
              {Array.from({ length: totalSteps }).map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-8 sm:w-12 rounded-full transition-colors ${
                    index + 1 <= currentStep ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of {totalSteps}
            </span>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-4">
          {renderStep()}
        </ScrollArea>

        <Separator />

        <div className="flex flex-col sm:flex-row justify-between gap-3">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="gap-2 min-h-[44px] order-2 sm:order-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          {currentStep < totalSteps ? (
            <Button onClick={handleNext} className="gap-2 min-h-[44px] order-1 sm:order-2">
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleLaunch} disabled={loading} className="gap-2 min-h-[44px] order-1 sm:order-2">
              <Rocket className="h-4 w-4" />
              {loading ? "Launching..." : "Launch Workflow"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
